import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { Seo } from "@/components/seo/Seo";
import { Hero } from "@/components/sections/Hero";
import { TrustBar } from "@/components/sections/TrustBar";
import { ServicesGrid } from "@/components/sections/ServicesGrid";
import { BeforeAfter } from "@/components/sections/BeforeAfter";
import { ReviewsWidget } from "@/components/sections/ReviewsWidget";
import { FAQAccordion } from "@/components/FAQAccordion";
import { CTAGroup } from "@/components/CTAGroup";
import { Button } from "@/components/ui/button";
import { BENEFITS, BRAND, FAQ_HOME, SERVICE_AREAS } from "@/data/site";
import { orgSchema, faqSchema } from "@/lib/schema";
import aboutTeamAsset from "@/assets/about-team-vrp-flooring.webp.asset.json";


const PROJECTS = [
  { src: "https://assets.cdn.filesafe.space/D8zHHsXQdNqiD1R03R0i/media/6a7b2fc0602fa7d1821ecdd7.jpg", alt: "Luxury vinyl plank flooring installed in a Connecticut home" },
  { src: "https://assets.cdn.filesafe.space/D8zHHsXQdNqiD1R03R0i/media/6a7b2fc0fe4291bd1085d146.jpg", alt: "Hardwood flooring installation in a Connecticut living space" },
  { src: "https://assets.cdn.filesafe.space/D8zHHsXQdNqiD1R03R0i/media/6a7b2fc0fe4291bd1085d14b.jpg", alt: "Laminate flooring project completed in Connecticut" },
];

const PROCESS = [
  { n: "01", title: "Free Consultation", desc: "We come to you with hundreds of samples, measure the space, and answer every question." },
  { n: "02", title: "Material Selection", desc: "Compare hardwood, vinyl, laminate and tile in your own lighting at the price point you want." },
  { n: "03", title: "Professional Installation", desc: "Furniture moved, subfloor prepped, floors installed clean — often the very next day." },
  { n: "04", title: "Final Walkthrough", desc: "We inspect every seam, transition and trim detail with you before we call it finished." },
];

const Index = () => {
  const [visualizerOn, setVisualizerOn] = useState(false);
  return (
    <Layout>
      <Seo
        title={`Flooring Contractor in Danbury, CT | ${BRAND.name}`}
        description="VRP Flooring LLC installs hardwood, vinyl, laminate and tile flooring across Fairfield, New Haven and Hartford County, CT. Licensed, insured, free estimates and next-day installation."
        jsonLd={[orgSchema(), faqSchema(FAQ_HOME)]}
      />

      <Hero />
      <TrustBar />

      {/* About */}
      <section className="container-page py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="eyebrow mb-4 text-accentInk">About VRP Flooring</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-5">
              Residential and commercial flooring specialists in Connecticut.
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              VRP Flooring LLC is a locally owned and operated flooring contractor based in Danbury, CT, specializing in the installation, replacement and renovation of residential and commercial floors. We shop with you in your own home or business — bringing hundreds of hardwood, vinyl, laminate and tile samples so you can see every color and texture in your own light.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              With same-day appointments, next-day installation and a 100% satisfaction guarantee, we've completed more than 3,000 flooring projects for homeowners, landlords and business owners across Fairfield, New Haven and Hartford County.
            </p>
            <CTAGroup />
          </div>
          <div>
            <img
              src={aboutTeamAsset.url}
              alt="VRP Flooring LLC team standing in front of company trucks in Danbury, Connecticut"
              className="rounded-lg shadow-elev w-full max-w-[480px] mx-auto"
              loading="lazy"
              width={1122}
              height={1402}
            />
          </div>
        </div>
      </section>

      {/* Room Visualizer */}
      <section className="container-page py-12 md:py-16">
        <div className="mx-auto w-full max-w-[1200px]">
          {visualizerOn ? (
            <iframe
              src="https://vrp-flooring-visualizer.lovable.app/"
              title="VRP Flooring Room Visualizer"
              width="100%"
              height="900"
              style={{ border: 0, width: "100%", display: "block", borderRadius: "12px" }}
              loading="lazy"
              allow="camera; clipboard-write"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : (
            <div className="rounded-xl border bg-secondary/40 px-6 py-14 text-center" style={{ minHeight: 380 }}>
              <h2 className="font-display text-2xl md:text-4xl font-bold mb-3">See new floors in your own room</h2>
              <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                Upload a photo of your space and preview hardwood, laminate, LVT and LVP options before you buy.
              </p>
              <Button size="lg" onClick={() => setVisualizerOn(true)}>Launch the room visualizer</Button>
            </div>
          )}
        </div>
      </section>


      {/* Services overview */}
      <section className="bg-secondary/40 py-20 md:py-28">
        <div className="container-page">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4 text-accentInk">Our Flooring Services</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Complete flooring services, from one trusted crew.</h2>
            <p className="text-muted-foreground text-lg">Hardwood flooring installation, vinyl flooring, laminate, tile, full floor replacement and commercial flooring — all installed by our own licensed team.</p>
          </div>
          <ServicesGrid />
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-foreground text-background py-20 md:py-28">
        <div className="container-page grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow mb-4">Why Choose VRP Flooring</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-6">The benefits of working with a local flooring contractor.</h2>
            <p className="text-background/80 text-lg leading-relaxed mb-8">
              Since 2001 we've helped Connecticut homeowners and businesses upgrade their floors without the mess, delays and surprises other contractors bring.
            </p>
            <ul className="space-y-4">
              {BENEFITS.map((b) => (
                <li key={b.title} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-accent mt-1 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-background">{b.title}</h3>
                    <p className="text-background/70 text-sm leading-relaxed">{b.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <CTAGroup variant="onDark" />
            </div>
          </div>
          <div>
            <BeforeAfter />
            <p className="text-center text-sm text-background/60 mt-3">Drag the slider — hardwood floor refinishing, Danbury CT</p>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="container-page py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Four simple steps. Zero surprises.</h2>
          <p className="text-muted-foreground text-lg">From the first sample to the final walkthrough, you'll always know what's next.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PROCESS.map((p) => (
            <div key={p.n} className="card-elev p-6">
              <div className="font-display text-4xl font-bold text-accentInk mb-3">{p.n}</div>
              <h3 className="font-semibold text-lg mb-2">{p.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Service Areas */}
      <section className="bg-secondary/40 py-20 md:py-28">
        <div className="container-page">
          <div className="max-w-2xl mb-10">
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Proudly serving Connecticut homes and businesses.</h2>
            <p className="text-muted-foreground text-lg">Headquartered in Danbury, we install flooring across Fairfield, New Haven and Hartford County.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {SERVICE_AREAS.map((a) => (
              <Link
                key={a.slug}
                to={`/service-areas/${a.slug}`}
                className="card-elev p-5 group flex items-center justify-between"
              >
                <div>
                  <div className="font-semibold text-foreground">{a.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{a.state}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-accent transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="container-page py-20 md:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <h2 className="font-display text-3xl md:text-5xl font-bold max-w-2xl">Recent floors we installed in Connecticut.</h2>
          <Link to="/gallery" className="text-sm font-semibold text-accentInk hover:underline inline-flex items-center">
            View full gallery <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PROJECTS.map((p) => (
            <figure key={p.alt} className="overflow-hidden rounded-lg">
              <img src={p.src} alt={p.alt} loading="lazy" width={1280} height={960} className="w-full aspect-[4/3] object-cover transition-transform duration-700 hover:scale-105" />
            </figure>
          ))}
        </div>
      </section>


      {/* Testimonials */}
      <section className="bg-secondary/40 py-20 md:py-28">
        <div className="container-page">
          <div className="max-w-2xl mb-12 text-center mx-auto">
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4 md:whitespace-nowrap">What our flooring customers say.</h2>
          </div>
          <ReviewsWidget />
          <div className="text-center mt-10">
            <Button asChild variant="outline" size="lg" className="font-semibold">
              <Link to="/reviews">Read All Reviews <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-page py-20 md:py-28">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-5xl font-bold">Quick answers to common flooring questions.</h2>
          </div>
          <FAQAccordion items={FAQ_HOME} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-dark text-background py-20 md:py-28">
        <div className="container-page text-center max-w-3xl">
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-5">Ready to Transform Your Floors?</h2>
          <p className="text-background/80 text-lg mb-8">Book your free in-home consultation today. Same-day appointments, next-day installation, no obligation.</p>
          <CTAGroup variant="onDark" className="justify-center" primaryLabel="Request a Free Estimate" />
        </div>
      </section>
    </Layout>
  );
};

export default Index;
